// Chrome APIs that Scramjet used to delete because, left alone, they would talk to the proxy's
// own origin or escape it. A real Chrome has all of them, and scripts check for them (a page
// without navigator.serviceWorker or window.navigation is not a normal Chrome), so they stay,
// made to behave as they would for a site that has never used them.

import { ScramjetClient } from "@client/index";
import { String } from "@/shared/snapshot";

export default function (client: ScramjetClient, self: any) {
	const { Promise, DOMException } = self;

	// Service workers: the frame runs under the proxy's own worker, which the page must neither
	// see nor replace. It looks like a site that has none registered, with registering refused
	// as when the browser blocks it for the site.
	client.Trap("ServiceWorkerContainer.prototype.controller", {
		get() {
			return null;
		},
	});
	const neverReady = new Promise(() => {});
	client.Trap("ServiceWorkerContainer.prototype.ready", {
		get() {
			return neverReady;
		},
	});
	client.Proxy("ServiceWorkerContainer.prototype.register", {
		apply(ctx) {
			ctx.return(
				Promise.reject(
					new DOMException(
						"Failed to register a ServiceWorker: The user denied permission to use Service Worker.",
						"SecurityError"
					)
				)
			);
		},
	});
	client.Proxy("ServiceWorkerContainer.prototype.getRegistration", {
		apply(ctx) {
			ctx.return(Promise.resolve(undefined));
		},
	});
	client.Proxy("ServiceWorkerContainer.prototype.getRegistrations", {
		apply(ctx) {
			ctx.return(Promise.resolve([]));
		},
	});

	// Navigation API: addresses in and out are the site's, not the proxy's.
	const unrewrite = (url: unknown) =>
		typeof url === "string" ? client.unrewriteUrl(url) : url;
	client.Trap(
		["NavigationHistoryEntry.prototype.url", "NavigationDestination.prototype.url"],
		{
			get(ctx) {
				return unrewrite(ctx.get());
			},
		}
	);
	client.Proxy("Navigation.prototype.navigate", {
		apply(ctx) {
			if (ctx.args.length > 0) {
				ctx.args[0] = client.rewriteUrl(String(ctx.args[0]), {
					navigateType: "location",
				});
			}
		},
	});

	// Cookie Store API, on the same cookies as document.cookie (the proxy's cookie jar).
	if (self.CookieStore) {
		const cookies = () => {
			const list = [];
			const text = client.context.cookieJar.getCookies(client.url, true);
			for (const part of text ? text.split("; ") : []) {
				const eq = part.indexOf("=");
				// Chrome's fields, in its order; the jar does not say how each was set.
				list.push({
					domain: null,
					expires: null,
					name: eq < 0 ? "" : part.slice(0, eq),
					partitioned: false,
					path: "/",
					sameSite: "lax",
					secure: false,
					value: eq < 0 ? part : part.slice(eq + 1),
				});
			}

			return list;
		};
		const nameOf = (args: any[]) =>
			args[0] && typeof args[0] === "object" ? args[0].name : args[0];
		const setCookie = (cookie: string) => {
			client.context.cookieJar.setCookies(cookie, client.url);
			client.init.sendSetCookie([{ url: client.url, cookie }]);
		};

		client.Proxy("CookieStore.prototype.get", {
			apply(ctx) {
				const name = nameOf(ctx.args);
				const all = cookies();
				const found =
					name === undefined ? all[0] : all.find((c) => c.name === String(name));
				ctx.return(Promise.resolve(found ?? null));
			},
		});
		client.Proxy("CookieStore.prototype.getAll", {
			apply(ctx) {
				const name = nameOf(ctx.args);
				const all = cookies();
				ctx.return(
					Promise.resolve(
						name === undefined ? all : all.filter((c) => c.name === String(name))
					)
				);
			},
		});
		client.Proxy("CookieStore.prototype.set", {
			apply(ctx) {
				const options =
					ctx.args[0] && typeof ctx.args[0] === "object"
						? ctx.args[0]
						: { name: ctx.args[0], value: ctx.args[1] };
				let cookie = `${options.name ?? ""}=${options.value ?? ""}; path=${options.path ?? "/"}`;
				if (options.domain) cookie += `; domain=${options.domain}`;
				if (options.expires != null)
					cookie += `; expires=${new Date(options.expires).toUTCString()}`;
				if (options.sameSite) cookie += `; samesite=${options.sameSite}`;
				if (client.url.protocol === "https:") cookie += "; secure";
				setCookie(cookie);
				ctx.return(Promise.resolve(undefined));
			},
		});
		client.Proxy("CookieStore.prototype.delete", {
			apply(ctx) {
				const options =
					ctx.args[0] && typeof ctx.args[0] === "object"
						? ctx.args[0]
						: { name: ctx.args[0] };
				let cookie = `${options.name ?? ""}=; path=${options.path ?? "/"}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
				if (options.domain) cookie += `; domain=${options.domain}`;
				setCookie(cookie);
				ctx.return(Promise.resolve(undefined));
			},
		});
	}

	// Cast screens connect to the address themselves, outside the proxy: there are none.
	for (const method of ["start", "reconnect"]) {
		client.Proxy(`PresentationRequest.prototype.${method}`, {
			apply(ctx) {
				ctx.return(
					Promise.reject(new DOMException("No screens found.", "NotFoundError"))
				);
			},
		});
	}
	// Protected Audience fetches its bidding scripts from the browser itself, outside the proxy.
	for (const method of ["joinAdInterestGroup", "leaveAdInterestGroup", "runAdAuction"]) {
		client.Proxy(`Navigator.prototype.${method}`, {
			apply(ctx) {
				ctx.return(Promise.resolve(method === "runAdAuction" ? null : undefined));
			},
		});
	}
}
